/**
 * Page back-office : Boîte à suggestions étudiants.
 * Accessible aux rôles admin/super_admin/directeur.
 */
import { useState } from 'react';
import {
  Inbox, MessageSquare, EyeOff, Search, CheckCircle, Archive,
  Filter, User, MapPin, BookOpen, AlertCircle, ChevronDown, X,
} from 'lucide-react';
import {
  useListerSuggestionsQuery,
  useTraiterSuggestionMutation,
  type SuggestionItem,
} from '../../services/suggestionsApi';

const CATEGORIES = [
  { v: 'cours',         label: 'Cours / Pédagogie' },
  { v: 'formateur',     label: 'Formateur' },
  { v: 'fonctionnement',label: "Fonctionnement de l'école" },
  { v: 'administration',label: 'Administration / Scolarité' },
  { v: 'infrastructure',label: 'Infrastructure / Locaux' },
  { v: 'autre',         label: 'Autre' },
];

const STATUT_META: Record<string, { label: string; classes: string }> = {
  nouvelle: { label: 'Nouvelle', classes: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  lue:      { label: 'Lue',      classes: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  traitee:  { label: 'Traitée',  classes: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
  archivee: { label: 'Archivée', classes: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
};

export default function Suggestions() {
  const [statut, setStatut] = useState<'nouvelle' | 'lue' | 'traitee' | 'archivee' | 'toutes'>('nouvelle');
  const [categorie, setCategorie] = useState('');
  const [anonyme, setAnonyme] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<SuggestionItem | null>(null);

  const { data, isFetching, refetch } = useListerSuggestionsQuery({
    statut, categorie: categorie || undefined, anonyme: anonyme || undefined,
  });
  const [traiter] = useTraiterSuggestionMutation();

  const filtre = (data?.results || []).filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      s.titre.toLowerCase().includes(q)
      || s.contenu.toLowerCase().includes(q)
      || s.nom_etudiant.toLowerCase().includes(q)
      || s.prenoms_etudiant.toLowerCase().includes(q)
      || (s.matricule_etudiant || '').toLowerCase().includes(q)
    );
  });

  const handleMarquer = async (s: SuggestionItem, nv: 'lue' | 'traitee' | 'archivee') => {
    try {
      await traiter({ id: s.id, statut: nv }).unwrap();
      if (active?.id === s.id) setActive({ ...active, statut: nv });
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <header className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Inbox size={22} className="text-blue-600 dark:text-blue-400" />
            Boîte à suggestions
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Avis et retours des étudiants — anonymes ou nominatifs.
          </p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="px-3 py-2 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
          {isFetching ? '…' : '↻ Actualiser'}
        </button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Nouvelles', n: data?.kpis.nouvelles, color: 'blue' },
          { label: 'Lues', n: data?.kpis.lues, color: 'gray' },
          { label: 'Traitées', n: data?.kpis.traitees, color: 'green' },
          { label: 'Archivées', n: data?.kpis.archivees, color: 'amber' },
        ].map(k => (
          <div key={k.label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{k.n ?? '—'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded p-1">
          {(['nouvelle', 'lue', 'traitee', 'archivee', 'toutes'] as const).map(s => (
            <button key={s} onClick={() => setStatut(s)}
              className={`px-3 py-1 text-xs rounded ${
                statut === s
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}>
              {s === 'toutes' ? 'Toutes' : STATUT_META[s]?.label || s}
            </button>
          ))}
        </div>
        <select value={categorie} onChange={(e) => setCategorie(e.target.value)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Toutes catégories</option>
          {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        <select value={anonyme} onChange={(e) => setAnonyme(e.target.value as any)}
          className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <option value="">Anonymes + Nominatives</option>
          <option value="true">Anonymes seulement</option>
          <option value="false">Nominatives seulement</option>
        </select>
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="pl-7 pr-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-48" />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filtre.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Inbox size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-400" />
            Aucune suggestion ne correspond aux filtres.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filtre.map(s => (
              <li key={s.id}>
                <button onClick={() => setActive(s)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUT_META[s.statut]?.classes}`}>
                          {s.statut_label}
                        </span>
                        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                          {s.categorie_label}
                        </span>
                        {s.anonyme && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded inline-flex items-center gap-0.5">
                            <EyeOff size={9} /> Anonyme
                          </span>
                        )}
                        {!s.anonyme && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded inline-flex items-center gap-0.5">
                            <User size={9} /> {s.nom_etudiant} {s.prenoms_etudiant}
                          </span>
                        )}
                        {s.classe_etudiant && (
                          <span className="text-[10px] text-gray-500">{s.classe_etudiant}</span>
                        )}
                      </div>
                      {s.titre && (
                        <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                          {s.titre}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
                        {s.contenu}
                      </div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(s.date_creation).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {s.pole && ` • ${s.pole}`}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal détail */}
      {active && (
        <ModalDetail
          item={active}
          onClose={() => setActive(null)}
          onMarquer={(nv) => handleMarquer(active, nv)}
        />
      )}
    </div>
  );
}

function ModalDetail({ item, onClose, onMarquer }: {
  item: SuggestionItem;
  onClose: () => void;
  onMarquer: (nv: 'lue' | 'traitee' | 'archivee') => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUT_META[item.statut]?.classes}`}>
                {item.statut_label}
              </span>
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                {item.categorie_label}
              </span>
            </div>
            <h3 className="font-bold text-base text-gray-900 dark:text-white">
              {item.titre || '(Sans titre)'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {/* Étudiant */}
          <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            {item.anonyme ? (
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300 text-sm">
                <EyeOff size={14} /> <span className="font-medium">Soumission anonyme</span>
                {item.pole && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto inline-flex items-center gap-1">
                    <MapPin size={10} /> {item.pole}
                  </span>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white inline-flex items-center gap-1.5">
                  <User size={13} /> {item.nom_etudiant} {item.prenoms_etudiant}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3 flex-wrap">
                  {item.matricule_etudiant && <span className="font-mono">{item.matricule_etudiant}</span>}
                  {item.classe_etudiant && <span>{item.classe_etudiant}</span>}
                  {item.pole && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {item.pole}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Cible spécifique */}
          {(item.cible_formateur_nom || item.cible_matiere) && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs">
              <div className="font-semibold text-amber-700 dark:text-amber-300 mb-1">Concerne :</div>
              {item.cible_formateur_nom && (
                <div className="text-gray-800 dark:text-gray-200">Formateur : {item.cible_formateur_nom}</div>
              )}
              {item.cible_matiere && (
                <div className="text-gray-800 dark:text-gray-200 inline-flex items-center gap-1">
                  <BookOpen size={11} /> {item.cible_matiere}
                </div>
              )}
            </div>
          )}

          {/* Contenu */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
              Message
            </div>
            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-relaxed">
              {item.contenu}
            </div>
          </div>

          {/* Trace de traitement */}
          {item.traitee_par_nom && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/30 px-3 py-2 rounded">
              Traité par <strong>{item.traitee_par_nom}</strong>
              {item.date_traitement && ` le ${new Date(item.date_traitement).toLocaleString('fr-FR')}`}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex gap-2 justify-end">
          {item.statut !== 'archivee' && (
            <button onClick={() => onMarquer('archivee')}
              className="px-3 py-1.5 text-xs bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 rounded inline-flex items-center gap-1">
              <Archive size={11} /> Archiver
            </button>
          )}
          {item.statut !== 'traitee' && (
            <button onClick={() => onMarquer('traitee')}
              className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1">
              <CheckCircle size={11} /> Marquer comme traitée
            </button>
          )}
          {item.statut === 'nouvelle' && (
            <button onClick={() => onMarquer('lue')}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">
              Marquer comme lue
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
